/**
 * Cart Gamification Manager - Optimized Version
 * Handles progress bar animations and milestone updates for cart gamification
 */
class CartGamification {
  constructor() {
    this.cartContainer = null;
    this.progressBar = null;
    this.goal1 = null;
    this.goal2 = null;
    this.isInitialized = false;
    this.hasCompletedGoal2 = false;
    this.updateTimeout = null;
    this.lastCartCount = null;

    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.cartContainer = document.querySelector('.free-shipping-bar');
    if (!this.cartContainer) return;

    this.progressBar = this.cartContainer.querySelector('.free-shipping-bar__progress');
    if (!this.progressBar) return;

    this.goal1 = parseInt(this.cartContainer.dataset.goalOne) || 5;
    this.goal2 = parseInt(this.cartContainer.dataset.goalTwo) || 8;

    this.setupEventListeners();
    this.isInitialized = true;
    this.updateCartGamification();
  }

  setupEventListeners() {
    const debouncedUpdate = () => {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = setTimeout(() => this.updateCartGamification(), 150);
    };

    // Cart update events
    ['cart:updated', 'cartUpdated', 'cart-updated'].forEach(event => {
      (event.includes(':') ? document : window).addEventListener(event, debouncedUpdate);
    });

    // DOM mutations observer
    const observer = new MutationObserver((mutations) => {
      if (mutations.some(m =>
        m.type === 'childList' && 
        m.target.classList?.contains('cart-items', 'side-cart') ||
        m.target.id === 'cart-drawer'
      )) {
        debouncedUpdate();
      }
    });

    document.querySelectorAll('.cart-items, .side-cart, #cart-drawer').forEach(container => {
      observer.observe(container, { childList: true, subtree: true });
    });
  }

  async updateCartGamification() {
    if (!this.isInitialized) return;

    try {
      const cart = await fetch('/cart.js').then(r => r.json());

      const cartCount = cart.items.reduce((acc, item) =>
        item.properties?._reward_product === 'true' ? acc : acc + item.quantity, 0
      );

      if (this.lastCartCount === cartCount) return;

      this.lastCartCount = cartCount;
      this.updateProgressBar(cartCount);
      this.updateMilestones(cartCount);
      this.cartContainer.dataset.cartCount = cartCount;

    } catch (error) {
      console.error('❌ Cart gamification error:', error);
    }
  }

  updateProgressBar(cartCount) {
    if (cartCount >= this.goal2 && this.hasCompletedGoal2) return;

    let percentage;
    if (cartCount < this.goal1) {
      percentage = (cartCount / this.goal1) * 25;
    } else if (cartCount < this.goal2) {
      percentage = 25 + ((cartCount - this.goal1) / (this.goal2 - this.goal1)) * 50;
    } else {
      percentage = 100;
    }

    this.progressBar.style.setProperty('--shipping-percent', `${percentage}%`);
  }

  updateMilestones(cartCount) {
    const milestones = [
      { container: '.free-shipping-bar__milestone--first', goal: this.goal1 },
      { container: '.free-shipping-bar__milestone--second', goal: this.goal2 }
    ];

    milestones.forEach(({ container, goal }, index) => {
      const el = this.cartContainer.querySelector(container);
      const icon = el?.querySelector('.milestone-icon');
      if (!el || !icon) return;

      const isReached = cartCount >= goal;
      el.setAttribute('data-reached', isReached);

      const iconType = isReached ? 'check' : 'dot';
      if (!icon.querySelector(`.milestone-${iconType}`)) {
        icon.innerHTML = `<img src="${this.getAssetUrl(isReached ? 'check-icon-red.svg' : 'gray-dot.svg')}" class="milestone-${iconType}" alt="Goal ${index + 1}${isReached ? ' Reached' : ''}">`;
      }
    });

    // Handle Goal 2 celebration
    if (cartCount >= this.goal2) {
      if (!this.hasCompletedGoal2) {
        this.hasCompletedGoal2 = true;
        this.triggerCelebration();
      }
    } else if (this.hasCompletedGoal2) {
      this.hasCompletedGoal2 = false;
      this.progressBar.classList.remove('celebration-complete');
    }
  }

  triggerCelebration() {
    if (!this.progressBar.hasAttribute('data-animated-to-full')) {
      this.progressBar.setAttribute('data-animated-to-full', 'true');
    }

    setTimeout(() => {
      this.progressBar.style.setProperty('--shipping-percent', '100%');
      this.progressBar.classList.add('celebration-complete');
    }, 800);
  }

  getAssetUrl(filename) {
    return window.THEME_ASSETS?.[filename] || `/assets/${filename}`;
  }

  reinitialize() {
    const previousState = this.hasCompletedGoal2;
    this.isInitialized = false;
    this.setup();
    this.hasCompletedGoal2 = previousState;
    setTimeout(() => this.updateCartGamification(), 50);
  }

  static triggerUpdate() {
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }
}

// Initialize
let cartGamificationInstance = null;

export function initCartGamification() {
  if (!cartGamificationInstance) {
    cartGamificationInstance = new CartGamification();
  }
  return cartGamificationInstance;
}

// Global exports
window.CartGamification = CartGamification;
window.updateCartGamification = CartGamification.triggerUpdate;
window.reinitializeCartGamification = () => cartGamificationInstance?.reinitialize();

export default CartGamification;
